// @ts-ignore
// import WorkerC from './WorkerCompress?worker';
// @ts-ignore
// import WorkerP from './WorkerPreview?worker';
import { uniqId } from '@/utils/picControler/functions';
import { convert, MessageData } from '@/utils/picControler/handler';
import { Dimension, ImageInfo, ImageItem } from '@/utils/picControler/ImageBase';
import Mimes from '@/utils/picControler/mimes';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import useHomeState from '../store/useSmaller';

/**
 * @description: 构造图片压缩信息
 * @author: KaifengLi
 * @param {ImageInfo} item
 * @version: v2.25.0
 * @Date: 2024-11-01 15:49:23
 */
export function createMessageData(item: ImageInfo, option?: any): MessageData {
  return {
    /**
     * Why not use the spread operator here?
     * Because it causes an error when used this way,
     * and the exact reason is unknown at the moment.
     *
     * error: `Uncaught (in promise) DOMException: Failed to execute 'postMessage' on 'Worker': #<Object> could not be cloned.`
     * Reproduction method: In the second upload, include the same images as in the first.
     */
    info: {
      key: item.key,
      name: item.name,
      blob: item.blob,
      width: item.width,
      height: item.height,
    },
    // option: toJS(option),
    option: option ? _.cloneDeep(option) : option,
  };
}

function useTransform() {
  const { list, setList, option, setCompressLoading } = useHomeState();
  // 跟踪创建的URL对象
  const createdUrls = useRef<string[]>([]);
  // 创建Worker实例引用
  const workerRef = useRef<Worker | null>(null);

  // 创建URL对象的包装函数
  const createObjectURL = (blob: Blob): string => {
    const url = URL.createObjectURL(blob);
    createdUrls.current.push(url);
    return url;
  };

  // 初始化Worker
  useEffect(() => {
    try {
      workerRef.current = new Worker(
        new URL('@/utils/picControler/imageProcessWorker.ts', import.meta.url),
      );

      // 处理Worker返回的结果
      workerRef.current.onmessage = (e) => {
        const { type, result } = e.data;
        if (type === 'compress_result' || type === 'preview_result') {
          const item = compressMessageImage(list.get(result.key), result);
          if (item) {
            list.set(item.key, item);
          }
        }
      };
    } catch (error) {
      console.error('Failed to initialize worker:', error);
    }

    return () => {
      // 组件卸载时终止Worker
      workerRef.current?.terminate();

      // 释放所有URL对象
      createdUrls.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.error('Error revoking URL:', e);
        }
      });
      createdUrls.current = [];
    };
  }, []);

  // 使用Worker处理图片
  const processImageWithWorker = (item: ImageInfo) => {
    if (!workerRef.current) return;

    const msg = createMessageData(item, option);
    workerRef.current.postMessage({ type: 'compress', data: msg });
    // 也可以同时请求预览图
    workerRef.current.postMessage({ type: 'preview', data: msg });
  };

  function createImageList(files: Array<File>, compress?: boolean) {
    const infoListPromise = files.map(async (file, index) => {
      const info: ImageItem = {
        key: uniqId(),
        name: file.name,
        blob: file,
        width: 0,
        height: 0,
        src: createObjectURL(file),
        sort: index,
      };

      // Due to createImageBitmap do not support SVG blob,
      // we should get dimension of SVG via Image
      if (file.type === Mimes.svg) {
        const { width, height } = await new Promise<Dimension>((resolve) => {
          const img = new Image();
          img.src = info.src;
          img.onload = () => {
            resolve({
              x: 0,
              y: 0,
              width: img.width,
              height: img.height,
            });
          };
        });
        info.width = width;
        info.height = height;
      }

      return info;
    });

    if (compress) {
      compressImagePromise(infoListPromise);
    }

    return infoListPromise;
  }

  const compressMessageImage = (value: any, data: any): ImageItem | undefined => {
    if (!value) return undefined;

    const item = _.cloneDeep(value);
    item.width = data.width;
    item.height = data.height;

    if (data.compress) {
      item.compress = {
        ...data.compress,
        src: createObjectURL(data.compress.blob),
      };
    }

    if (data.preview) {
      item.preview = {
        ...data.preview,
        src: createObjectURL(data.preview.blob),
      };
    }

    return item;
  };

  function message(event: any) {
    const value = list.get(event.data.key);
    if (!value) return;

    const item = compressMessageImage(value, event.data) as ImageItem;
    if (!item) return;

    list.set(item.key, item);
  }

  const compressImagePromise = async (promise: Array<Promise<ImageItem>>) => {
    const compressPromises = await Promise.all(promise);
    setCompressLoading(true);

    // 批量处理，每次处理5个图片
    const batchSize = 5;
    const allBatchPromises = [];

    for (let i = 0; i < compressPromises.length; i += batchSize) {
      const batch = compressPromises.slice(i, i + batchSize);

      // 先添加到列表
      batch.forEach((item) => {
        list.set(item.key, item);
      });

      // 创建当前批次的处理Promise
      const batchPromise = async () => {
        // 使用Worker或降级到主线程处理
        if (workerRef.current) {
          batch.forEach((item) => processImageWithWorker(item));
          // 等待一小段时间让Worker开始处理
          await new Promise((resolve) => {
            setTimeout(resolve, 100);
          });
        }
        // 降级到主线程处理
        const tasks = batch.map(async (item: ImageItem) => {
          const newlist = createMessageData(item);
          const compressOutput = await convert(newlist, 'compress');
          const previewOutput = await convert(newlist, 'preview');
          if (compressOutput) {
            message({ data: compressOutput });
          }
          if (previewOutput) {
            message({ data: previewOutput });
          }
        });
        return Promise.all(tasks);
      };

      allBatchPromises.push(batchPromise());
    }

    // 等待所有批次处理完成
    await Promise.all(allBatchPromises);

    setCompressLoading(false);
    const updatedList = new Map<number, ImageItem>();
    list.forEach((item) => {
      updatedList.set(item.key, item);
    });
    setList(updatedList);
  };

  return {
    createImageList,
    compressMessageImage,
    compressImagePromise,
  };
}

export default useTransform;
